import {
  Alert,
  Button,
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  closeCurrentDialog,
  showNotification,
} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';
import {object} from 'yup';

import {useGetUserQuery, useInitializeUserMutation, usePatchUserMutation} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {handleApiError, yupPasswordValidation} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

type FormValues = {
  password: string;
  confirmPassword: string;
};

interface EditUserPasswordFormProps extends RequiredTestIdProps {
  userId: string;
}

export function EditUserPasswordForm(props: EditUserPasswordFormProps) {
  const [patchUser] = usePatchUserMutation();
  const [initializeUser, {isLoading: isInitializingUser}] = useInitializeUserMutation();

  const {data: user} = useGetUserQuery({id: props.userId});

  const isInvited = user?.state === 'invited';

  const handleSubmit: FormSubmitHandler<FormValues> = async (data) => {
    await patchUser({
      userId: props.userId,
      updateUserRequestBody: {
        password: data.password,
      },
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  const handleOnboardUser = () => {
    initializeUser({userId: props.userId})
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.user.labels.initializedSuccessfully')))
      .catch(handleApiError);
  };

  return (
    <Form<FormValues> schema={schema} onSubmit={handleSubmit}>
      {(control) => (
        <VStack spacing={4}>
          <Show when={isInvited}>
            <DataStatus isLoading={isInitializingUser} minHeight={11}>
              <Alert
                variant="warning"
                message={i18n.t('general.notifications.onboardAlert')}
                hyperlinks={[
                  {title: i18n.t('general.actions.onboard'), onClick: handleOnboardUser},
                ]}
              />
            </DataStatus>
          </Show>
          <Grid columns={2}>
            <FormField
              control={control}
              name="password"
              label={i18n.t('general.labels.password')}
              type="password"
              isDisabled={isInvited}
              data-testid={suffixTestId('edit-password', props)}
            />

            <FormField
              control={control}
              name="confirmPassword"
              label={i18n.t('general.labels.confirmPassword')}
              type="password"
              isDisabled={isInvited}
              data-testid={suffixTestId('edit-confirmPassword', props)}
            />
          </Grid>

          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={i18n.t('general.actions.discard')}
              data-testid={suffixTestId('edit-discard', props)}
              onClick={closeCurrentDialog}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.save')}
              data-testid={suffixTestId('edit-create', props)}
              variant="primary"
              isDisabled={isInvited}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}

const schema = object({
  password: yupPasswordValidation,
  confirmPassword: yupPasswordValidation.test(
    'passwords-match',
    i18n.t('general.notifications.passwordsDoesNotMatch'),
    (value, context) => context.parent.password === value
  ),
});
